import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { Check, Loader2, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

const Checkbox = React.forwardRef(
  ({ checked, indeterminate = false, className, onChange, ...props }, ref) => {
    const isActive = checked || indeterminate;
    const nextChecked = indeterminate ? true : !checked;

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-[7px] border transition-colors',
          isActive
            ? 'border-primary bg-primary text-white'
            : 'border-border/70 bg-background text-transparent hover:border-primary/50',
          className
        )}
        onClick={(event) => {
          event.stopPropagation();
          onChange?.({
            target: { checked: nextChecked },
          });
        }}
        {...props}
      >
        {indeterminate ? (
          <Minus className="h-5 w-5 stroke-[3.25] text-white" />
        ) : checked ? (
          <Check className="h-5 w-5 stroke-[3.25] text-white" />
        ) : null}
      </button>
    );
  }
);

Checkbox.displayName = 'Checkbox';

const RolloutCatalogTable = ({ table, refreshing = false }) => (
  <div className="relative overflow-hidden rounded-lg border border-border/35 bg-card/25">
    {refreshing ? (
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[3] flex justify-center p-3">
        <div className="inline-flex items-center gap-2 rounded-full border border-border/40 bg-background/92 px-3 py-1.5 text-[12px] font-medium text-muted-foreground shadow-sm backdrop-blur">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          Updating results...
        </div>
      </div>
    ) : null}

    <div className="themed-slim-scrollbar overflow-x-auto">
      <table
        className="min-w-full w-full table-fixed border-separate border-spacing-0"
        style={{ width: `max(100%, ${table.getTotalSize()}px)` }}
      >
        <thead className="sticky top-0 z-[1] bg-card/96 backdrop-blur">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className={cn(
                    'relative border-b border-border/30 px-4 py-3 text-left align-middle text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground',
                    header.column.id === 'select' && 'w-12',
                    header.column.id === 'download' && 'w-[156px]',
                    header.column.id === 'bugs' && 'w-[104px]'
                  )}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                  {header.column.getCanResize() ? (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={cn(
                        'absolute right-0 top-1 bottom-1 w-2 cursor-col-resize touch-none select-none',
                        'after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border/45',
                        header.column.getIsResizing() && 'after:bg-primary'
                      )}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={cn(
                'transition-colors',
                row.getCanExpand() && 'cursor-pointer hover:bg-primary/4',
                !row.getCanExpand() && 'cursor-default',
                row.depth > 0 &&
                  (row.getCanExpand()
                    ? 'bg-muted/10 hover:bg-muted/20'
                    : 'bg-muted/10'),
                row.getIsSelected() && 'bg-primary/5'
              )}
              aria-expanded={
                row.getCanExpand() ? row.getIsExpanded() : undefined
              }
              onClick={() => {
                if (row.getCanExpand()) {
                  row.toggleExpanded();
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cn(
                    'border-b border-border/25 px-4 py-4 align-middle text-[13px] text-foreground',
                    cell.column.id === 'select' && 'w-12',
                    cell.column.id === 'download' && 'w-[156px]',
                    cell.column.id === 'bugs' && 'w-[104px]'
                  )}
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export { Checkbox };
export default RolloutCatalogTable;
